import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { fileService } from '../services/api';

// Función para normalizar tags (igual que en el backend)
const normalizeTag = (tag) => {
  if (!tag || typeof tag !== 'string') return null;
  return tag.toLowerCase().trim().replace(/\s+/g, '-');
};

const FolderTagsModal = ({ isOpen, onClose, folder, onTagsUpdated }) => {
  const [selectedTags, setSelectedTags] = useState([]);
  const [predefinedTagsCategory1, setPredefinedTagsCategory1] = useState([]);
  const [predefinedTagsCategory2, setPredefinedTagsCategory2] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [loadingPredefined, setLoadingPredefined] = useState(false);

  // Cargar tags predefinidos cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      loadPredefinedTags();
    }
  }, [isOpen]);

  // Cargar tags existentes de la carpeta cuando se abre el modal
  useEffect(() => {
    if (isOpen && folder && (predefinedTagsCategory1.length > 0 || predefinedTagsCategory2.length > 0)) {
      loadFolderTags();
    } else if (!isOpen) {
      setSelectedTags([]);
    }
  }, [isOpen, folder, predefinedTagsCategory1, predefinedTagsCategory2]);

  const loadPredefinedTags = async () => {
    setLoadingPredefined(true);
    try {
      const response = await fileService.getPredefinedFolderTags();
      const category1Tags = response.category1 || [];
      const category2Tags = response.category2 || [];
      setPredefinedTagsCategory1(category1Tags);
      setPredefinedTagsCategory2(category2Tags);
    } catch (error) {
      console.error('Error cargando tags predefinidos de carpetas:', error);
      alert('Error al cargar tags predefinidos: ' + error.message);
    } finally {
      setLoadingPredefined(false);
    }
  };

  const loadFolderTags = async () => {
    const folderName = folder?.Key || folder?.name || '';
    if (!folderName) return;

    setLoadingTags(true);
    try {
      const folderTags = await fileService.getFolderTags(folderName);
      // Los tags vienen normalizados del backend, necesitamos mapearlos a los originales
      const normalizedFolderTags = folderTags.map(tag => normalizeTag(tag));
      
      // Combinar ambas categorías para buscar
      const allPredefinedTags = [...predefinedTagsCategory1, ...predefinedTagsCategory2];
      
      // Encontrar los tags originales que coinciden con los normalizados
      const matchingTags = allPredefinedTags.filter(tag => 
        normalizedFolderTags.includes(normalizeTag(tag))
      );
      
      setSelectedTags(matchingTags);
    } catch (error) {
      console.error('Error cargando tags de carpeta:', error);
      setSelectedTags([]);
    } finally {
      setLoadingTags(false);
    }
  };

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        // Remover tag
        return prev.filter(t => t !== tag);
      } else {
        // Agregar tag (máximo 1)
        if (prev.length >= 1) {
          alert('Solo se puede seleccionar máximo 1 tag');
          return prev;
        }
        return [...prev, tag];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const folderName = folder?.Key || folder?.name || '';
      // Enviar los tags seleccionados (se normalizarán en el backend)
      await fileService.updateFolderTags(folderName, selectedTags);
      
      if (onTagsUpdated) {
        // Enviar tags normalizados para consistencia
        const normalizedTags = selectedTags.map(tag => normalizeTag(tag));
        onTagsUpdated(folderName, normalizedTags);
      }

      onClose();
    } catch (error) {
      console.error('Error al actualizar tags de carpeta:', error);
      alert('Error al actualizar tags: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const folderName = folder?.displayName || folder?.Key || folder?.name || '';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Editar etiquetas de carpeta">
      <form onSubmit={handleSubmit} className="space-y-4">
        {loadingPredefined ? (
          <div className="text-sm text-gray-400">Cargando tags disponibles...</div>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Selecciona la etiqueta ({selectedTags.length}/1)
              </label>
              
              {loadingTags ? (
                <div className="text-sm text-gray-400 mb-4">Cargando tags de la carpeta...</div>
              ) : null}
              
              <div className="max-h-96 overflow-y-auto border border-gray-600 rounded-md p-4 bg-gray-800 space-y-4">
                {/* Categoría 1 - Amarillo */}
                {predefinedTagsCategory1.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-400 mb-2">Tag carpeta</p>
                    <div className="flex flex-wrap gap-2">
                      {predefinedTagsCategory1.map((tag) => {
                        const isSelected = selectedTags.includes(tag);
                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => handleTagToggle(tag)}
                            disabled={loading || loadingTags}
                            className={`
                              inline-flex items-center px-3 py-2 rounded-full text-sm font-medium border
                              transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-500/40
                              ${isSelected
                                ? 'bg-yellow-600 text-white border-yellow-500 shadow-lg shadow-yellow-500/25'
                                : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600 hover:text-white hover:border-gray-500'
                              }
                              ${(loading || loadingTags) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                            `}
                          >
                            {tag}
                            {isSelected && (
                              <span className="ml-2 text-xs">✓</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Categoría 2 - Verde */}
                {predefinedTagsCategory2.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-400 mb-2">Tag Tecweek</p>
                    <div className="flex flex-wrap gap-2">
                      {predefinedTagsCategory2.map((tag) => {
                        const isSelected = selectedTags.includes(tag);
                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => handleTagToggle(tag)}
                            disabled={loading || loadingTags}
                            className={`
                              inline-flex items-center px-3 py-2 rounded-full text-sm font-medium border
                              transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500/40
                              ${isSelected
                                ? 'bg-green-600 text-white border-green-500 shadow-lg shadow-green-500/25'
                                : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600 hover:text-white hover:border-gray-500'
                              }
                              ${(loading || loadingTags) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                            `}
                          >
                            {tag}
                            {isSelected && (
                              <span className="ml-2 text-xs">✓</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              {selectedTags.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-400 mb-2">Tags seleccionados:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedTags.map((tag) => {
                      const isCategory2 = predefinedTagsCategory2.includes(tag);
                      return (
                        <span
                          key={tag}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${
                            isCategory2
                              ? 'bg-green-100 text-green-800 border-green-200'
                              : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                          }`}
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleTagToggle(tag)}
                            className={`ml-2 ${isCategory2 ? 'text-green-600 hover:text-green-800' : 'text-yellow-600 hover:text-yellow-800'}`}
                            disabled={loading}
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading || loadingPredefined}
            className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-600 hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading || loadingTags || loadingPredefined}
            className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-md transition-colors disabled:opacity-50"
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default FolderTagsModal;

